import { useState } from "react";
import { Brain, X, Loader, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
} from "@mui/material";
import { useSubscriptionLimit } from "../../hooks/useSubscriptionLimit";
import { settingsPath } from "../../containers/Router";
import { useNavigate } from "react-router-dom";
import SubscriptionLimitDialog from "../SubscriptionLimitDialog";

const AIDocumentQA = ({
  userId,
  documentName,
  projectId,
  documentId,
  documentType,
  service,
}: any) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modelAPIKey, setModelAPIKey] = useState("");

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => navigate(settingsPath, { replace: true }));

  const handleAskQuestion = async () => {
    if (!question.trim()) return;

    setLoading(true);
    setError("");
    setAnswer("");

    try {
      await makeSubscriptionGatedRequest({
        requestFn: () =>
          service.answerQuestionOnDocument(
            userId,
            projectId,
            documentId,
            question,
            modelAPIKey
          ),
        onSuccess: (response: any) => {
          setAnswer(response.answer);
        },
        onError: (error: any) => {
          console.error(
            "Failed to answer question on the given document:",
            error
          );
          let errorMsg = error.error ? error.error : undefined;
          setError(
            errorMsg ||
              "Failed to get an answer. Please try again and/or contact support."
          );
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="outlined"
        startIcon={<Brain size={18} />}
        sx={{
          gap: 1,
          textTransform: "none",
          borderRadius: "8px",
          borderColor: "rgba(220, 38, 38, 0.5)",
          color: "#DC2626",
          fontWeight: 500,
          px: 2.5,
          py: 1,
          "&:hover": {
            borderColor: "#DC2626",
            backgroundColor: "rgba(220, 38, 38, 0.04)",
          },
        }}
      >
        Ask AI About This {documentType}
      </Button>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
            background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.2))",
                boxShadow: "0 2px 5px rgba(220, 38, 38, 0.1)",
              }}
            >
              <Brain className="h-5 w-5 text-red-600" />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#1F2937", fontSize: "1.1rem" }}
            >
              Ask AI About: {documentName}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              },
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText
            sx={{
              mt: 2,
              mb: 2,
              color: "#6B7280",
              fontSize: "0.9rem",
            }}
          >
            Ask a question about this {documentType.toLowerCase()} (up to 200
            characters)
          </DialogContentText>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              placeholder="Enter Model API Key (required)"
              value={modelAPIKey}
              onChange={(e) => setModelAPIKey(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: "rgba(0,0,0,0.1)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0,0,0,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#DC2626",
                    borderWidth: "1px",
                  },
                },
              }}
            />
            <TextField
              placeholder={`What would you like to know about this ${documentType.toLowerCase()}?`}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              inputProps={{ maxLength: 200 }}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "rgba(0,0,0,0.1)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0,0,0,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#DC2626",
                    borderWidth: "1px",
                  },
                },
              }}
            />

            <Button
              onClick={handleAskQuestion}
              disabled={loading || !question.trim()}
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                py: 1.25,
                background: "linear-gradient(to right, #DC2626, #EF4444)",
                boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
                "&:hover": {
                  background: "linear-gradient(to right, #B91C1C, #DC2626)",
                  boxShadow: "0 3px 6px rgba(220, 38, 38, 0.3)",
                },
                "&.Mui-disabled": {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                },
              }}
              startIcon={
                loading ? (
                  <Loader size={18} className="animate-spin" />
                ) : (
                  <Brain size={18} />
                )
              }
            >
              {loading ? "Getting Answer..." : "Ask Question"}
            </Button>

            {error && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  "& .MuiAlert-icon": {
                    alignItems: "center",
                  },
                }}
                icon={<AlertCircle size={16} />}
              >
                {error}
              </Alert>
            )}

            {answer && (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  maxHeight: 300,
                  overflowY: "auto",
                  borderRadius: "10px",
                  borderColor: "rgba(220, 38, 38, 0.2)",
                  background: "linear-gradient(to bottom, #FFFFFF, #F9FAFB)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color: "#1F2937",
                    lineHeight: 1.6,
                  }}
                >
                  {answer}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <SubscriptionLimitDialog {...dialogProps} />
    </>
  );
};

export default AIDocumentQA;
